import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  useReactFlow,
  ReactFlowProvider,
  Handle,
  NodeToolbar,
  Position,
  Node,
  Edge,
  MarkerType,
  getNodesBounds,
  getViewportForBounds,
} from '@xyflow/react';
import { toPng } from 'html-to-image';
import { AutoComplete, Button, Form, Input, Modal, Popconfirm, Select, TreeSelect, message } from 'antd';
import { Helmet } from 'react-helmet-async';
import '@xyflow/react/dist/style.css';
import {
  Building2,
  UserCheck,
  Download,
  Maximize2,
  ZoomIn,
  ZoomOut,
  ChevronDown,
  X,
  Search,
  Plus,
  Minus,
  Pencil,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import UserSidebar, { USER_PAGE_ROW } from '../../components/SidebarNew/UserSidebar';
import Layout from '../../Layout';
import { UserAvatar } from '../Social/component/UserAvatar';
import { usePermission } from '../../hooks/usePermission';
import { mixGetV1 } from '../../services/mixin';
import { SEED_EXPANDED } from './orgSeed';
import useOrgStore from './orgStore';
import {
  buildPositionTree, canHold, collectEmployees, countEmployees, deleteNode, filterLeaders, findNode, getAncestorsAndSelf, isLeader, norm, parentOf, parentOptions, planDrop, reorderDept, saveNode, searchOrg, staffIdsOnChart, suggestRole, type DropPlan, type RawNode,
} from './orgTree';

// ==========================================
// 2. MODAL DANH SÁCH NHÂN SỰ CỦA PHÒNG BAN (cả phòng ban con)
// ==========================================
const MembersModal = ({
  isOpen,
  onClose,
  department,
  onPickEmployee,
}: {
  isOpen: boolean;
  onClose: () => void;
  department: RawNode | null;
  onPickEmployee: (id: string) => void;
}) => {
  const [q, setQ] = useState('');
  useEffect(() => { setQ(''); }, [department?.id]);
  if (!isOpen || !department) return null;

  const all = collectEmployees(department);
  const k = q.trim().toLowerCase();
  const members = k ? all.filter(m => `${m.node.name} ${m.node.role}`.toLowerCase().includes(k)) : all;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/65 p-3 sm:p-4" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-[92vw] max-w-md max-h-[80vh] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-700" role="dialog" aria-modal="true">
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 dark:text-slate-100 text-sm truncate pr-2">{department.label}</h3>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-slate-400 font-medium">{all.length} nhân sự</span>
            <button onClick={onClose} aria-label="Đóng" className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full cursor-pointer">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        {all.length > 5 && (
          <div className="px-3 pt-3">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Tìm theo tên, chức danh..." autoFocus
              className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-800 dark:text-slate-100 outline-none focus:border-orange-500" />
          </div>
        )}
        <div className="overflow-y-auto divide-y divide-slate-100 dark:divide-slate-700/60 p-2">
          {members.length === 0 && <div className="text-xs text-slate-400 text-center py-6">{all.length ? 'Không tìm thấy nhân sự' : 'Phòng ban chưa có nhân sự'}</div>}
          {members.map(({ node: m, path }) => (
            <button key={m.id} type="button" onClick={() => onPickEmployee(m.id)}
              className="w-full text-left flex items-center gap-3 p-2.5 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg transition cursor-pointer">
              <UserAvatar name={m.name || ''} src={m.avatar} size={36} />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{m.name}</div>
                <div className="text-[11px] text-slate-400 truncate">{[m.role || 'Nhân viên', ...path.slice(1)].join(' · ')}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Thẻ thông tin nhân sự khi bấm vào một người trên sơ đồ
const EmployeeCard = ({ emp, path, onClose, onShowDept }: { emp: RawNode; path: string[]; onClose: () => void; onShowDept: () => void }) => (
  <div className="absolute top-3 right-3 z-40 w-[280px] max-w-[calc(100%-1.5rem)] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl p-4">
    <button onClick={onClose} aria-label="Đóng" className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"><X className="w-4 h-4" /></button>
    <div className="flex items-center gap-3">
      <UserAvatar name={emp.name || ''} src={emp.avatar} size={48} />
      <div className="min-w-0">
        <div className="font-bold text-slate-900 dark:text-slate-100 truncate">{emp.name}</div>
        <div className="text-xs text-slate-500 truncate">{emp.role || 'Nhân viên'}</div>
      </div>
    </div>
    <div className="mt-3 text-xs text-slate-500 dark:text-slate-400 flex items-start gap-1.5">
      <Building2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <span>{path.join(' › ')}</span>
    </div>
    {isLeader(emp.role) && <span className="inline-block mt-2 text-[10px] font-bold uppercase tracking-wide bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300 px-2 py-0.5 rounded">Quản lý</span>}
    {path.length > 0 && (
      <button onClick={onShowDept} className="mt-3 w-full text-xs font-semibold text-orange-600 border border-orange-200 dark:border-orange-900 rounded-lg py-1.5 hover:bg-orange-50 dark:hover:bg-orange-950/30 cursor-pointer">
        Xem cả phòng {path[path.length - 1]}
      </button>
    )}
  </div>
);

// ==========================================
// 3. THÊM, SỬA, XÓA
// ==========================================
type EditTarget = { mode: 'add' | 'edit'; type: RawNode['type']; node: RawNode };
const ROLE_OPTIONS = ['Trưởng phòng', 'Lead', 'Nhân viên'].map(value => ({ value }));
const unitName = (n: RawNode) => n.label || n.name || '';
const matchTitle = (q: string, n: any) => norm(String(n.title)).includes(norm(q));

interface StaffOption { id: number; name: string; position?: string; avatar?: string; department?: { name: string } }

const OrgFormModal = ({ root, target, onClose, onSaved }: { root: RawNode; target: EditTarget; onClose: () => void; onSaved: (tree: RawNode, id: string) => void }) => {
  const [form] = Form.useForm();
  const { mode, type, node } = target;
  const isEdit = mode === 'edit';
  const isDept = type === 'department';
  const fixed = isEdit && !!node.kind;
  const parentId = isEdit ? parentOf(root, node.id)?.id : node.id;
  // Mục mẫu chưa gắn hồ sơ: ô chọn hiện tên cũ cho tới khi chọn người khác
  const currentStaff = isEdit ? node.staffId ?? node.name : undefined;

  const [staffs, setStaffs] = useState<StaffOption[]>([]);
  const [staffState, setStaffState] = useState<'loading' | 'ready' | 'failed'>('loading');
  useEffect(() => {
    if (isDept) return;
    let alive = true;
    mixGetV1('/api/v1/be/staffs', { limit: 200 })
      .then(res => {
        if (!alive) return;
        setStaffs(res?.status ? res.data?.items || res.data || [] : []);
        setStaffState(res?.status ? 'ready' : 'failed');
      })
      .catch(() => { if (alive) setStaffState('failed'); });
    return () => { alive = false; };
  }, [isDept]);

  const staffOptions = useMemo(() => {
    const used = staffIdsOnChart(root, isEdit ? node.id : undefined);
    return staffs.map(x => ({
      value: x.id,
      label: [x.name, x.position, x.department?.name].filter(Boolean).join(' · '),
      disabled: used.includes(x.id),
    }));
  }, [staffs, root, isEdit, node.id]);

  const pickStaff = (id: number) => {
    const picked = staffs.find(x => x.id === id);
    if (picked?.position) form.setFieldValue('role', picked.position);
  };

  const submit = (v: { label?: string; staff?: number | string; role?: string; parentId?: string }) => {
    const base = { id: isEdit ? node.id : undefined, type, parentId: v.parentId };
    const picked = staffs.find(x => x.id === v.staff);
    // Không đổi người thì giữ nguyên tên, ảnh đang có; danh sách không tải được vẫn sửa được chức danh, đơn vị
    const person = v.staff === currentStaff ? { name: node.name, staffId: node.staffId, avatar: node.avatar } : { name: picked?.name, staffId: picked?.id, avatar: picked?.avatar };
    const r = saveNode(root, isDept ? { ...base, label: v.label } : { ...base, ...person, role: v.role });
    if (r.ok) onSaved(r.tree, r.id);
    else form.setFields([{ name: isDept ? 'label' : 'staff', errors: [r.error] }]);
  };

  const title = fixed ? `Sửa ${unitName(node)}` : `${isEdit ? 'Sửa' : 'Thêm'} ${isDept ? 'phòng ban' : 'nhân sự'}`;
  return (
    <Modal open title={title} onCancel={onClose} onOk={() => form.submit()} okText={isEdit ? 'Lưu' : 'Thêm'} cancelText="Hủy"
      afterOpenChange={o => { if (o) form.getFieldInstance(isDept ? 'label' : 'staff')?.focus(); }}>
      <Form form={form} layout="vertical" requiredMark={false} onFinish={submit} className="pt-2"
        initialValues={{ label: isEdit ? node.label : '', staff: currentStaff, role: isEdit ? node.role : suggestRole(node), parentId }}>
        {isDept ? (
          <Form.Item name="label" label={fixed && node.kind === 'company' ? 'Tên công ty' : fixed ? 'Tên ban' : 'Tên phòng ban'} rules={[{ required: true, whitespace: true, message: 'Nhập tên' }]}>
            <Input maxLength={100} onPressEnter={() => form.submit()} />
          </Form.Item>
        ) : (
          <>
            <Form.Item name="staff" label="Nhân viên" rules={[{ required: true, message: 'Chọn nhân viên' }]}
              help={staffState === 'failed' ? 'Không tải được hồ sơ nhân viên, hãy thử mở lại' : undefined} validateStatus={staffState === 'failed' ? 'error' : undefined}>
              <Select showSearch placeholder="Chọn từ hồ sơ nhân viên" loading={staffState === 'loading'} options={staffOptions} onChange={pickStaff}
                filterOption={(q, o) => norm(String(o?.label)).includes(norm(q))}
                notFoundContent={staffState === 'loading' ? 'Đang tải hồ sơ nhân viên...' : 'Không tìm thấy nhân viên'} />
            </Form.Item>
            <Form.Item name="role" label="Chức danh" rules={[{ required: true, whitespace: true, message: 'Nhập chức danh' }]}>
              <AutoComplete options={ROLE_OPTIONS} maxLength={100} placeholder="Trưởng phòng, Lead, Nhân viên..." filterOption={false} />
            </Form.Item>
          </>
        )}
        {!fixed && (
          <Form.Item name="parentId" label={isDept ? 'Thuộc' : 'Thuộc phòng ban hoặc báo cáo cho'} rules={[{ required: true, message: 'Chọn nơi trực thuộc' }]} className="!mb-1">
            <TreeSelect showSearch treeDefaultExpandAll filterTreeNode={matchTitle} treeData={parentOptions(root, type, isEdit ? node.id : undefined)} popupMatchSelectWidth={false} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

const OrgDeleteModal = ({ root, node, onClose, onDeleted }: { root: RawNode; node: RawNode; onClose: () => void; onDeleted: (tree: RawNode, parentId: string) => void }) => {
  const parent = parentOf(root, node.id);
  const [moveTo, setMoveTo] = useState<string | undefined>(parent?.id);
  const kids = node.children || [];
  const isDept = node.type === 'department';
  const needsMove = isDept && kids.length > 0;

  const confirm = () => {
    const r = deleteNode(root, node.id, moveTo);
    if (r.ok) onDeleted(r.tree, r.id);
    else message.error(r.error);
  };

  return (
    <Modal open title={`Xóa ${unitName(node)}?`} onCancel={onClose} onOk={confirm} okText="Xóa" cancelText="Hủy" okButtonProps={{ danger: true, disabled: needsMove && !moveTo }}>
      {!isDept && <p>{kids.length ? `${kids.length} người báo cáo trực tiếp sẽ chuyển lên ${parent ? unitName(parent) : ''}.` : `${node.name} sẽ bị bỏ khỏi sơ đồ.`}</p>}
      {isDept && !needsMove && <p>Phòng ban này không có nhân sự.</p>}
      {needsMove && (
        <>
          <p className="mb-2">Phòng ban còn {countEmployees(node)} nhân sự. Toàn bộ nhân sự và phòng ban con sẽ chuyển sang:</p>
          <TreeSelect aria-label="Chuyển sang" className="w-full" showSearch treeDefaultExpandAll filterTreeNode={matchTitle} value={moveTo} onChange={setMoveTo} treeData={parentOptions(root, 'department', node.id)} popupMatchSelectWidth={false} />
        </>
      )}
    </Modal>
  );
};

/** Nút thao tác hiện phía trên mục đang chọn. NodeToolbar nằm ngoài khung xuất ảnh nên không lẫn vào file PNG */
const NodeActions = ({ data }: any) => {
  const a = data.actions;
  if (!a || !data.isSelected) return null;
  const canAdd = a.canCreate;
  const btn = 'flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700';
  const fire = (e: React.MouseEvent, fn: () => void) => { e.stopPropagation(); fn(); };
  const label = data.label || data.name;
  return (
    <NodeToolbar isVisible position={Position.Top} offset={6}>
      <div className="flex items-center gap-0.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-0.5 text-slate-700 dark:text-slate-200">
        {canAdd && data.canHoldDept && (
          <button type="button" className={btn} aria-label={`Thêm phòng ban vào ${label}`} onClick={e => fire(e, () => a.onAdd(data.id, 'department'))}><Plus className="w-3 h-3" />Phòng ban</button>
        )}
        {canAdd && data.canHoldEmp && (
          <button type="button" className={btn} aria-label={`Thêm nhân sự vào ${label}`} onClick={e => fire(e, () => a.onAdd(data.id, 'employee'))}><Plus className="w-3 h-3" />{data.type === 'employee' ? 'Cấp dưới' : 'Nhân sự'}</button>
        )}
        {a.canEdit && (
          <button type="button" className={btn} aria-label={`Sửa ${label}`} onClick={e => fire(e, () => a.onEdit(data.id))}><Pencil className="w-3 h-3" />Sửa</button>
        )}
        {a.canDelete && !data.kind && (
          <button type="button" className={`${btn} text-rose-600 dark:text-rose-400`} aria-label={`Xóa ${label}`} onClick={e => fire(e, () => a.onDelete(data.id))}><Trash2 className="w-3 h-3" />Xóa</button>
        )}
      </div>
    </NodeToolbar>
  );
};

// ==========================================
// 4. CUSTOM NODES
// ==========================================
const DepartmentNode = ({ data }: any) => {
  const isSelected = data.isSelected;

  return (
    <div
      onClick={() => data.onSelectNode?.(data.id)}
      title={data.canDrag ? 'Kéo để sắp xếp' : undefined}
      className={`group relative px-3 sm:px-4 py-2 sm:py-2.5 bg-white dark:bg-slate-800 rounded-full flex items-center justify-between w-auto min-w-[150px] sm:min-w-[210px] max-w-[260px] sm:max-w-[320px] shadow-sm border transition-all ${data.canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${
        isSelected || data.isDropTarget
          ? 'border-2 border-orange-500 ring-4 ring-orange-100 dark:ring-orange-950/40'
          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
      }`}
    >
      <NodeActions data={data} />
      <Handle type="target" position={Position.Left} className="!bg-transparent !border-0 !w-1 !h-1" />

      <span className="font-semibold text-[11px] sm:text-xs text-slate-800 dark:text-slate-100 uppercase tracking-wide mr-2 break-words whitespace-normal leading-relaxed select-none">
        {data.label}
      </span>

      <div className="flex items-center gap-1.5 shrink-0 self-center">
        {data.count !== undefined && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              data.onOpenMembers?.(data);
            }}
            className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[10px] sm:text-[11px] font-bold text-white transition-transform active:scale-95 cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-slate-300 dark:hover:ring-slate-600 ${
              data.badgeColor || 'bg-emerald-500'
            }`}
          >
            {data.count}
          </button>
        )}
      </div>

      {data.hasChildren && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            data.onToggleExpand?.();
          }}
          className="opacity-95 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity absolute -right-2 customer-h-center w-4 h-4 rounded-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:border-orange-500 hover:text-orange-500 text-slate-500 dark:text-slate-400 flex items-center justify-center shadow-sm cursor-pointer z-20"
        >
          {data.isExpanded ? <Minus className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
        </button>
      )}

      <Handle type="source" position={Position.Right} className="!bg-transparent !border-0 !w-1 !h-1" />
    </div>
  );
};

const EmployeeNode = ({ data }: any) => {
  const isSelected = data.isSelected;

  return (
    <div
      onClick={() => data.onSelectNode?.(data.id)}
      className={`group relative px-2.5 sm:px-3 py-1.5 bg-white dark:bg-slate-800 rounded-full flex items-center gap-2 w-auto min-w-[150px] sm:min-w-[210px] max-w-[260px] sm:max-w-[320px] shadow-sm border transition-all cursor-pointer ${
        isSelected
          ? 'border-2 border-orange-500 ring-4 ring-orange-100 dark:ring-orange-950/40'
          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
      }`}
    >
      <NodeActions data={data} />
      <Handle type="target" position={Position.Left} className="!bg-transparent !border-0 !w-1 !h-1" />

      <UserAvatar name={data.name || ''} src={data.avatar} size={30} className="border border-slate-200 dark:border-slate-700" />
      <div className="flex flex-col text-left min-w-0">
        <span className="font-bold text-[11px] sm:text-xs text-slate-800 dark:text-slate-100 leading-tight truncate">{data.name}</span>
        <span className="text-[9px] sm:text-[10px] text-slate-400 dark:text-slate-500 font-medium truncate">{data.role}</span>
      </div>

      {data.hasChildren && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            data.onToggleExpand?.();
          }}
          className="opacity-95 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity absolute -right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:border-orange-500 hover:text-orange-500 text-slate-500 dark:text-slate-400 flex items-center justify-center shadow-sm cursor-pointer z-20"
        >
          {data.isExpanded ? <Minus className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
        </button>
      )}

      <Handle type="source" position={Position.Right} className="!bg-transparent !border-0 !w-1 !h-1" />
    </div>
  );
};

// Vạch báo vị trí sẽ chèn khi kéo đổi thứ tự
const DropLineNode = () => <div className="w-[150px] sm:w-[210px] h-1 rounded-full bg-orange-500 pointer-events-none" />;

const nodeTypes = {
  department: DepartmentNode,
  employee: EmployeeNode,
  dropLine: DropLineNode,
};

// ==========================================
// 5. LAYOUT CÂY
// ==========================================
function buildTreeLayout(
  root: RawNode,
  expandedIds: Set<string>,
  selectedNodeId: string | null,
  onToggleExpand: (id: string) => void,
  onOpenMembers: (dept: any) => void,
  onSelectNode: (id: string) => void,
  isMobile: boolean = false,
  isDarkMode: boolean = false,
  actions?: { canEdit: boolean }
) {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  
  const xSpacing = isMobile ? 180 : 300;
  const ySpacing = isMobile ? 45 : 65;

  let currentY = 10;
  const edgeColor = isDarkMode ? '#475569' : '#cbd5e1';

  function processNode(item: RawNode, depth: number, parentId: string | null): number {
    const hasChildren = Boolean(item.children && item.children.length > 0);
    const isExpanded = expandedIds.has(item.id);

    let nodeY = 0;
    // Máy tính mới kéo được; điện thoại kéo là để di chuyển sơ đồ, đổi đơn vị bằng nút Sửa
    const canDrag = item.type === 'department' && !item.kind && !isMobile && Boolean(actions?.canEdit);

    if (hasChildren && isExpanded && item.children) {
      const childYs: number[] = [];
      item.children.forEach((child) => {
        childYs.push(processNode(child, depth + 1, item.id));
      });
      nodeY = (childYs[0] + childYs[childYs.length - 1]) / 2;
    } else {
      nodeY = currentY;
      currentY += ySpacing;
    }

    nodes.push({
      id: item.id,
      type: item.type,
      position: { x: depth * xSpacing + (isMobile ? 5 : 30), y: nodeY },
      draggable: canDrag,
      data: {
        ...item,
        canDrag,
        count: item.type === 'department' ? countEmployees(item) : undefined,
        hasChildren,
        isExpanded,
        isSelected: item.id === selectedNodeId,
        onToggleExpand: () => onToggleExpand(item.id),
        onOpenMembers,
        onSelectNode,
        actions,
        canHoldDept: canHold(item, 'department'),
        canHoldEmp: canHold(item, 'employee'),
      },
    });

    if (parentId) {
      edges.push({
        id: `e-${parentId}-${item.id}`,
        source: parentId,
        target: item.id,
        type: 'smoothstep',
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 8,
          height: 8,
          color: edgeColor,
        },
        style: { stroke: edgeColor, strokeWidth: 1.5 },
      });
    }

    return nodeY;
  }

  processNode(root, 0, null);
  return { nodes, edges };
}

// ==========================================
// 6. MAIN CANVAS CONTROLLER
// ==========================================
const DEFAULT_EXPANDED_DEPT = SEED_EXPANDED;
const DEFAULT_EXPANDED_POS = ['pos-root', 'pos-level-0', 'pos-level-1'];

const OrgCanvas = () => {
  const { zoomIn, zoomOut, fitView, setCenter, getZoom, getNode, getNodes, getInternalNode, screenToFlowPosition } = useReactFlow();
  const orgTree = useOrgStore(s => s.tree);
  const setOrgTree = useOrgStore(s => s.setTree);
  const resetOrg = useOrgStore(s => s.reset);
  const { can } = usePermission('org');
  const canCreate = can('create');
  const canEdit = can('edit');
  const canDelete = can('delete');

  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const viewDropdownRef = useRef<HTMLDivElement>(null);

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const checkDark = () => setIsDarkMode(document.documentElement.classList.contains('dark'));
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as globalThis.Node)) setShowSearchPopover(false);
      if (viewDropdownRef.current && !viewDropdownRef.current.contains(event.target as globalThis.Node)) setShowViewDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, []);

  const [viewType, setViewType] = useState<'department' | 'position'>('department');
  const [onlyLeader, setOnlyLeader] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() =>
    new Set(typeof window !== 'undefined' && window.innerWidth < 768 ? ['root-company', 'root-bgd', 'phong-ba'] : DEFAULT_EXPANDED_DEPT));

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>('phong-ba');
  const [selectedDeptForModal, setSelectedDeptForModal] = useState<RawNode | null>(null);
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [showSearchPopover, setShowSearchPopover] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [zoomLevel, setZoomLevel] = useState(100);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [deleting, setDeleting] = useState<RawNode | null>(null);
  const [drag, setDrag] = useState<{ id: string; dx: number; dy: number; plan: DropPlan | null } | null>(null);

  // Cây đang hiển thị: theo phòng ban hoặc theo chức vụ, có thể chỉ giữ quản lý
  const tree = useMemo(() => {
    const base = onlyLeader ? filterLeaders(orgTree)! : orgTree;
    return viewType === 'position' ? buildPositionTree(base, orgTree.label || '') : base;
  }, [orgTree, viewType, onlyLeader]);

  // Chỉ sửa trên cây đầy đủ: cây theo chức vụ là bản tổng hợp, chế độ Leader đang ẩn bớt người
  const editable = viewType === 'department' && !onlyLeader;
  const canManage = canCreate || canEdit || canDelete;

  const changeView = (v: 'department' | 'position') => {
    setViewType(v);
    setExpandedIds(new Set(v === 'position' ? DEFAULT_EXPANDED_POS : DEFAULT_EXPANDED_DEPT));
    setSelectedNodeId(null);
    setShowViewDropdown(false);
    setTimeout(() => fitView({ padding: 0.2, duration: 400 }), 80);
  };

  const handleToggleExpand = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleOpenMembers = useCallback((dept: any) => {
    setSelectedDeptForModal(findNode(tree, dept.id));
  }, [tree]);

  const centerOn = useCallback((id: string, delay = 100) => {
    setTimeout(() => {
      const n = getNode(id);
      if (n) setCenter(n.position.x + 100, n.position.y + 20, { duration: 500, zoom: getZoom() });
    }, delay);
  }, [getNode, setCenter, getZoom]);

  const handleSelectNode = useCallback((id: string) => {
    setSelectedNodeId(id);
    centerOn(id);
  }, [centerOn]);

  const actions = useMemo(() => (editable && canManage ? {
    canCreate, canEdit, canDelete,
    onAdd: (id: string, type: RawNode['type']) => { const node = findNode(orgTree, id); if (node) setEditTarget({ mode: 'add', type, node }); },
    onEdit: (id: string) => { const node = findNode(orgTree, id); if (node) setEditTarget({ mode: 'edit', type: node.type, node }); },
    onDelete: (id: string) => setDeleting(findNode(orgTree, id)),
  } : undefined), [editable, canManage, canCreate, canEdit, canDelete, orgTree]);

  const { nodes, edges } = useMemo(
    () => buildTreeLayout(tree, expandedIds, selectedNodeId, handleToggleExpand, handleOpenMembers, handleSelectNode, isMobile, isDarkMode, actions),
    [tree, expandedIds, selectedNodeId, handleToggleExpand, handleOpenMembers, handleSelectNode, isMobile, isDarkMode, actions]
  );

  // Khi đang kéo: cả nhánh đi theo con trỏ, kèm vạch chèn hoặc viền nơi thả
  const shownNodes = useMemo(() => {
    if (!drag) return nodes;
    const self = findNode(orgTree, drag.id);
    const intoId = drag.plan?.mode === 'into' ? drag.plan.targetId : null;
    const list: Node[] = nodes.map(n => {
      if (self && getAncestorsAndSelf(self, n.id)) return { ...n, position: { x: n.position.x + drag.dx, y: n.position.y + drag.dy }, zIndex: 1000, style: { opacity: 0.85 } };
      return n.id === intoId ? { ...n, data: { ...n.data, isDropTarget: true } } : n;
    });
    if (drag.plan?.mode === 'order') list.push({ id: '__drop-line', type: 'dropLine', position: { x: drag.plan.lineX, y: drag.plan.lineY - 2 }, draggable: false, selectable: false, data: {} });
    return list;
  }, [nodes, drag, orgTree]);

  const handleNodeDrag = (e: MouseEvent | TouchEvent, node: Node) => {
    const base = nodes.find(n => n.id === node.id);
    if (!base) return;
    // Kích thước thật chỉ có ở node nội bộ vì sơ đồ không nhận onNodesChange
    const rects = getNodes().map(n => { const m = getInternalNode(n.id)?.measured; return { id: n.id, x: n.position.x, y: n.position.y, w: m?.width || 0, h: m?.height || 0 }; });
    const at = 'touches' in e ? e.changedTouches[0] : e;
    const plan = planDrop(orgTree, node.id, screenToFlowPosition({ x: at.clientX, y: at.clientY }), rects);
    setDrag({ id: node.id, dx: node.position.x - base.position.x, dy: node.position.y - base.position.y, plan });
  };

  const handleNodeDragStop = () => {
    const done = drag;
    setDrag(null);
    if (!done?.plan) return;
    const { id, plan } = done;
    if (plan.mode === 'order') {
      setOrgTree(reorderDept(orgTree, id, plan.beforeId));
      setSelectedNodeId(id);
      return;
    }
    const self = findNode(orgTree, id);
    const target = findNode(orgTree, plan.targetId);
    if (!self || !target) return;
    Modal.confirm({
      title: `Chuyển ${self.label} vào ${target.label}?`,
      content: countEmployees(self) ? `${countEmployees(self)} nhân sự của phòng sẽ đi theo.` : undefined,
      okText: 'Chuyển',
      cancelText: 'Hủy',
      onOk: () => {
        const r = saveNode(orgTree, { id, type: 'department', parentId: target.id, label: self.label });
        if (!r.ok) { message.error(r.error); return; }
        setOrgTree(r.tree);
        reveal(id, r.tree);
        message.success('Đã chuyển phòng ban');
      },
    });
  };

  const hits = useMemo(() => searchOrg(tree, searchQuery), [tree, searchQuery]);

  /** Mở các nhánh cha rồi đưa nút (phòng ban hoặc nhân sự) vào giữa màn hình */
  const reveal = (id: string, root: RawNode = tree) => {
    const path = getAncestorsAndSelf(root, id);
    if (!path) return;
    setExpandedIds(prev => {
      const next = new Set(prev);
      path.slice(0, -1).forEach(p => next.add(p));
      return next;
    });
    setSelectedNodeId(id);
    centerOn(id, 150);
  };

  // Nhân sự đang chọn → hiện thẻ thông tin
  const selectedEmployee = selectedNodeId ? findNode(tree, selectedNodeId) : null;
  const employeePath = selectedEmployee?.type === 'employee'
    ? (getAncestorsAndSelf(tree, selectedEmployee.id) || []).slice(0, -1).map(id => findNode(tree, id)).filter((n): n is RawNode => !!n && n.type === 'department').map(n => n.label || '')
    : [];

  const afterSave = (next: RawNode, id: string) => {
    setOrgTree(next);
    setEditTarget(null);
    reveal(id, next);
    message.success(editTarget?.mode === 'edit' ? 'Đã lưu thay đổi' : 'Đã thêm vào sơ đồ');
  };

  const afterDelete = (next: RawNode, parentId: string) => {
    setOrgTree(next);
    setDeleting(null);
    reveal(parentId, next);
    message.success('Đã xóa khỏi sơ đồ');
  };

  const handleZoomIn = () => zoomIn({ duration: 300 });
  const handleZoomOut = () => zoomOut({ duration: 300 });
  const handleFitView = () => fitView({ padding: isMobile ? 0.15 : 0.2, duration: 500 });

  /** Xuất toàn bộ sơ đồ đang mở ra ảnh PNG */
  const exportPng = async () => {
    const viewport = wrapRef.current?.querySelector('.react-flow__viewport') as HTMLElement | null;
    if (!viewport) return;
    setExporting(true);
    try {
      const bounds = getNodesBounds(getNodes());
      const pad = 40;
      const width = Math.min(6000, Math.ceil(bounds.width + 260 + pad * 2));
      const height = Math.min(6000, Math.ceil(bounds.height + 60 + pad * 2));
      const vp = getViewportForBounds(bounds, width, height, 0.2, 2, pad);
      const dataUrl = await toPng(viewport, {
        backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
        width, height, pixelRatio: 2,
        style: { width: `${width}px`, height: `${height}px`, transform: `translate(${vp.x}px, ${vp.y}px) scale(${vp.zoom})` },
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `so-do-to-chuc-${viewType === 'position' ? 'theo-chuc-vu' : 'theo-phong-ban'}-${new Date().toISOString().slice(0, 10)}.png`;
      a.click();
      message.success('Đã xuất ảnh sơ đồ');
    } catch {
      message.error('Không xuất được ảnh sơ đồ');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div ref={wrapRef} className="w-full h-full relative bg-slate-50 dark:bg-slate-900 font-sans overflow-hidden transition-colors duration-200">
      <ReactFlow
        nodes={shownNodes}
        edges={edges}
        onNodeDrag={handleNodeDrag}
        onNodeDragStop={handleNodeDragStop}
        nodeDragThreshold={5}
        nodeTypes={nodeTypes}
        minZoom={0.2}
        maxZoom={2}
        defaultViewport={{ x: 10, y: 10, zoom: isMobile ? 0.38 : 0.85 }}
        nodesDraggable={false}
        panOnDrag={true}
        zoomOnScroll={true}
        zoomOnPinch={true}
        panOnScroll={false}
        preventScrolling={true}
        onPaneClick={() => {
          setShowSearchPopover(false);
          setShowViewDropdown(false);
          setSelectedNodeId(null);
        }}
        onMove={(_, viewport) => setZoomLevel(Math.round(viewport.zoom * 100))}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} color={isDarkMode ? '#334155' : '#cbd5e1'} />
      </ReactFlow>

      {/* Chú thích chế độ đang xem */}
      {(viewType === 'position' || onlyLeader) && (
        <div className="absolute top-3 left-3 z-30 text-[11px] font-semibold bg-white/90 dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1 text-slate-600 dark:text-slate-300">
          {viewType === 'position' ? 'Theo chức vụ' : 'Theo phòng ban'}{onlyLeader ? ' · chỉ hiện quản lý' : ''}
          {canManage && <span className="font-normal text-slate-500 dark:text-slate-400"> · Về Theo phòng ban, tắt Leader để thêm, sửa, xóa</span>}
        </div>
      )}

      {selectedEmployee?.type === 'employee' && (
        <EmployeeCard
          emp={selectedEmployee}
          path={employeePath}
          onClose={() => setSelectedNodeId(null)}
          onShowDept={() => {
            const ids = getAncestorsAndSelf(tree, selectedEmployee.id) || [];
            const dept = ids.map(id => findNode(tree, id)).reverse().find(n => n?.type === 'department');
            if (dept) setSelectedDeptForModal(dept);
          }}
        />
      )}

      <MembersModal
        isOpen={Boolean(selectedDeptForModal)}
        onClose={() => setSelectedDeptForModal(null)}
        department={selectedDeptForModal}
        onPickEmployee={id => { setSelectedDeptForModal(null); reveal(id); }}
      />

      {drag && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-40 max-w-[90%] text-xs font-semibold bg-slate-900 text-white dark:bg-white dark:text-slate-900 rounded-lg px-3 py-1.5 shadow-lg pointer-events-none">
          {drag.plan?.mode === 'into' ? `Thả để chuyển vào ${findNode(orgTree, drag.plan.targetId)?.label}`
            : drag.plan ? 'Thả để đổi vị trí tại vạch cam'
            : 'Kéo lên xuống để đổi vị trí, thả lên phòng ban khác để chuyển vào đó'}
        </div>
      )}

      {editTarget && <OrgFormModal root={orgTree} target={editTarget} onClose={() => setEditTarget(null)} onSaved={afterSave} />}
      {deleting && <OrgDeleteModal root={orgTree} node={deleting} onClose={() => setDeleting(null)} onDeleted={afterDelete} />}

      {/* THANH CÔNG CỤ (nằm trong khung sơ đồ) */}
      <div className="absolute bottom-4 inset-x-0 z-40 flex justify-center px-2 pointer-events-none">
      <div className="pointer-events-auto flex items-center bg-white/95 dark:bg-slate-800/95 backdrop-blur border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg px-2 py-1.5 gap-0.5 sm:gap-1 text-xs text-slate-700 dark:text-slate-200 select-none max-w-full">
        {/* Chế độ xem */}
        <div ref={viewDropdownRef} className="relative shrink-0">
          <button
            onClick={() => { setShowViewDropdown(!showViewDropdown); setShowSearchPopover(false); }}
            className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition font-medium cursor-pointer"
            title="Chế độ xem"
          >
            {viewType === 'department' ? <Building2 className="w-4 h-4 text-slate-500 shrink-0" /> : <UserCheck className="w-4 h-4 text-slate-500 shrink-0" />}
            <span className="hidden md:inline">{viewType === 'department' ? 'Theo phòng ban' : 'Theo chức vụ'}</span>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          </button>
          {showViewDropdown && (
            <div className="absolute bottom-11 left-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl p-1 w-44 flex flex-col gap-0.5 z-50">
              {([['department', Building2, 'Theo phòng ban'], ['position', UserCheck, 'Theo chức vụ']] as const).map(([v, Icon, label]) => (
                <button key={v} onClick={() => changeView(v)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-left hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer ${viewType === v ? 'bg-slate-100 dark:bg-slate-700 font-semibold text-slate-800 dark:text-slate-100' : ''}`}>
                  <Icon className="w-4 h-4 text-slate-500" /> {label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="w-[1px] h-4 bg-slate-200 dark:bg-slate-700 mx-0.5 sm:mx-1 shrink-0" />

        {/* Chỉ hiện quản lý */}
        <label className="flex items-center gap-1.5 sm:gap-2 px-1.5 sm:px-2 py-1 cursor-pointer select-none shrink-0" title="Chỉ hiện trưởng / phó / quản lý">
          <span className="text-slate-600 dark:text-slate-300 font-medium flex items-center gap-1 text-[11px] sm:text-xs">
            <span className="border border-slate-300 dark:border-slate-600 rounded px-1 text-[9px] sm:text-[10px]">A|Z</span>
            <span className="hidden sm:inline">Leader</span>
          </span>
          <input type="checkbox" checked={onlyLeader} onChange={e => { setOnlyLeader(e.target.checked); setSelectedNodeId(null); setTimeout(() => fitView({ padding: 0.2, duration: 400 }), 80); }} className="sr-only peer" />
          <div className="w-8 h-5 bg-slate-200 dark:bg-slate-700 rounded-full peer peer-checked:after:translate-x-[12px] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 dark:after:border-slate-600 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-orange-500 relative"></div>
        </label>
        <div className="w-[1px] h-4 bg-slate-200 dark:bg-slate-700 mx-0.5 sm:mx-1 shrink-0" />

        {/* Tìm phòng ban / nhân sự */}
        <div ref={searchRef} className="relative shrink-0">
          <button
            onClick={() => { setShowSearchPopover(!showSearchPopover); setShowViewDropdown(false); }}
            className={`p-1.5 sm:p-2 rounded-lg transition cursor-pointer ${showSearchPopover ? 'bg-slate-100 dark:bg-slate-700 text-orange-600 font-bold' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'}`}
            title="Tìm phòng ban, nhân sự"
          >
            <Search className="w-4 h-4" />
          </button>
          {showSearchPopover && (
            <div className="absolute bottom-11 left-1/2 -translate-x-1/2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl p-3 w-[min(320px,80vw)] flex flex-col gap-2 z-50">
              <div className="relative flex items-center">
                <input
                  type="text" placeholder="Tìm phòng ban hoặc nhân sự..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus
                  className="w-full pl-3 pr-8 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:border-orange-500"
                />
                <Search className="w-4 h-4 text-slate-400 absolute right-2.5 pointer-events-none" />
              </div>
              <div className="px-1 pt-1 font-semibold text-xs text-slate-700 dark:text-slate-300 uppercase tracking-wide">{orgTree.label}</div>
              <div className="max-h-60 overflow-y-auto flex flex-col gap-0.5 pr-1">
                {hits.length > 0 ? hits.map(h => (
                  <button key={h.id} onClick={() => { reveal(h.id); setShowSearchPopover(false); }}
                    className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition flex items-center gap-1.5 cursor-pointer ${selectedNodeId === h.id ? 'bg-orange-50 dark:bg-orange-950/30 font-semibold text-orange-600' : 'hover:bg-slate-100 dark:hover:bg-slate-700/60 text-slate-700 dark:text-slate-200'}`}>
                    {!searchQuery && <span className="text-slate-400 font-normal select-none">{'—'.repeat(Math.min(h.depth, 3))}{h.depth ? ' ' : ''}</span>}
                    {h.kind === 'employee' ? <UserCheck className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                    <span className="min-w-0">
                      <span className="block truncate">{h.label}</span>
                      {searchQuery && <span className="block truncate text-[10px] text-slate-400 font-normal">{h.sub}</span>}
                    </span>
                  </button>
                )) : <div className="text-xs text-slate-400 text-center py-4">Không tìm thấy “{searchQuery}”</div>}
              </div>
            </div>
          )}
        </div>

        <button onClick={exportPng} disabled={exporting} className="hidden sm:flex p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-600 dark:text-slate-300 cursor-pointer shrink-0 disabled:opacity-50" title="Xuất ảnh PNG">
          <Download className="w-4 h-4" />
        </button>

        {/* Nạp lại là bỏ hết phòng ban, nhân sự đã thêm: cần quyền Xóa */}
        {canDelete && (
          <Popconfirm title="Nạp lại sơ đồ mẫu?" description="Mọi thay đổi trên sơ đồ sẽ mất." okText="Nạp lại" cancelText="Hủy"
            onConfirm={() => { resetOrg(); setSelectedNodeId(null); setExpandedIds(new Set(DEFAULT_EXPANDED_DEPT)); message.success('Đã nạp lại sơ đồ mẫu'); }}>
            <Button type="text" size="small" className="!hidden sm:!inline-flex shrink-0" icon={<RotateCcw className="w-4 h-4 text-slate-600 dark:text-slate-300" />} title="Nạp lại sơ đồ mẫu" aria-label="Nạp lại sơ đồ mẫu" />
          </Popconfirm>
        )}

        <div className="w-[1px] h-4 bg-slate-200 dark:bg-slate-700 mx-0.5 sm:mx-1 shrink-0" />

        <button onClick={handleFitView} className="p-1.5 sm:p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-600 dark:text-slate-300 cursor-pointer shrink-0" title="Gom vừa màn hình"><Maximize2 className="w-4 h-4" /></button>
        <button onClick={handleZoomOut} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-600 dark:text-slate-300 cursor-pointer shrink-0" title="Thu nhỏ"><ZoomOut className="w-4 h-4" /></button>
        <span className="hidden sm:inline-block w-9 text-center font-semibold text-slate-700 dark:text-slate-200 shrink-0">{zoomLevel}%</span>
        <button onClick={handleZoomIn} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-600 dark:text-slate-300 cursor-pointer shrink-0" title="Phóng to"><ZoomIn className="w-4 h-4" /></button>
      </div>
      </div>
    </div>
  );
};

export default function OrganizationalChart() {
  return (
    <Layout>
      <Helmet><title>Sơ đồ tổ chức | Khothi</title></Helmet>
      <div className={USER_PAGE_ROW}>
        <UserSidebar />
        <div className="w-full min-w-0 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <div className="w-full h-[calc(100vh-140px)] min-h-[520px] relative">
            <ReactFlowProvider>
              <OrgCanvas />
            </ReactFlowProvider>
          </div>
        </div>
      </div>
    </Layout>
  );
}
